/**
 * Wave 29.10.l7 — Supplier price-list importer.
 *
 * Two surfaces:
 *   `<SupplierPricelistUploader supplierId onCommitted>`
 *     ── Single-file dropzone + scan trigger. Hands the resulting draft
 *        off to `<SupplierPricelistReview>`.
 *   `<SupplierPricelistReview draft onCommitted onCancel>`
 *     ── Big table of OCR'd lines, match badges, bulk-pricing controls,
 *        commit button.
 *
 * Renders inside the Supplier 360 page (see SupplierDetail.jsx).
 */
import { useEffect, useMemo, useState } from "react";
import { Upload, Loader2, Check, X, AlertTriangle, Edit3, FileText } from "lucide-react";
import api from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { toast } from "./ui/sonner";

export function SupplierPricelistUploader({ supplierId, onCommitted }) {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [draft, setDraft] = useState(null);
  const [summary, setSummary] = useState(null);

  const handleScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      // Compress images >1MB; pass PDFs/CSVs through untouched.
      const sendable = file.type?.startsWith("image/") ? await compressImage(file) : file;
      const fd = new FormData();
      fd.append("supplier_id", supplierId);
      fd.append("file", sendable);
      const { data } = await api.post("/supplier-pricelist/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 3 * 60 * 1000,
      });
      if (!data?.draft?.lines?.length) {
        toast.warning(data?.warning || "No line items extracted. Try a clearer scan or upload as CSV/Excel.");
        return;
      }
      setDraft(data.draft);
      setSummary(data.summary);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally { setScanning(false); }
  };

  if (draft) {
    return (
      <SupplierPricelistReview
        draft={draft}
        summary={summary}
        onCancel={() => { setDraft(null); setFile(null); setSummary(null); }}
        onCommitted={(totals) => {
          setDraft(null); setFile(null); setSummary(null);
          onCommitted?.(totals);
        }}
      />
    );
  }

  return (
    <div className="hv-card p-4 space-y-3" data-testid="supplier-pricelist-uploader">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#1FB6C1]" />
        <h3 className="font-semibold text-white text-sm">Import a supplier price list</h3>
      </div>
      <p className="text-[11px] text-[#9aa3b5]">
        Drop a PDF or photo of the price sheet (OCR via Hidro), or a CSV/Excel for direct parsing.
        Each line is matched against your inventory — confirm matches, set a bulk markup, commit.
      </p>
      <label className="hv-btn hv-btn-ghost text-xs cursor-pointer w-fit" data-testid="pricelist-file-pick">
        <Upload className="w-3.5 h-3.5" />
        {file ? file.name : "Choose file"}
        <input
          type="file"
          className="hidden"
          accept=".pdf,.csv,.xlsx,.xls,image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </label>
      {file && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleScan}
            disabled={scanning}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="pricelist-scan-btn"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            {scanning ? "Reading price list…" : "Scan price list"}
          </button>
          <button type="button" onClick={() => setFile(null)} className="text-[11px] text-[#647087] hover:text-white">
            Clear
          </button>
          <span className="text-[10px] text-[#647087]">{(file.size / 1024).toFixed(0)} KB</span>
        </div>
      )}
    </div>
  );
}

function SupplierPricelistReview({ draft, summary, onCancel, onCommitted }) {
  const [lines, setLines] = useState(draft.lines || []);
  const [pricingMode, setPricingMode] = useState("markup"); // markup | gp | fixed | none
  const [pricingValue, setPricingValue] = useState(40);
  const [defaultReorder, setDefaultReorder] = useState("");
  const [defaultVat, setDefaultVat] = useState(15);
  const [committing, setCommitting] = useState(false);

  const stats = useMemo(() => {
    const inc = lines.filter((l) => l.include !== false);
    const nu = inc.filter((l) => l.decision === "new").length;
    const up = inc.filter((l) => l.decision === "update").length;
    const cc = inc.filter((l) => l.cost_changed).length;
    const mc = inc.filter((l) => l.cost == null).length;
    return { total: lines.length, included: inc.length, new: nu, update: up, cost_changed: cc, missing_cost: mc };
  }, [lines]);

  const updateLine = (idx, patch) => {
    setLines((rows) => rows.map((r) => (r.row_idx === idx ? { ...r, ...patch } : r)));
  };

  const computeSell = (cost) => {
    if (cost == null) return null;
    if (pricingMode === "markup" && pricingValue) return +(cost * (1 + pricingValue / 100)).toFixed(2);
    if (pricingMode === "gp" && pricingValue != null && pricingValue < 100) return +(cost / (1 - pricingValue / 100)).toFixed(2);
    if (pricingMode === "fixed" && pricingValue != null) return +Number(pricingValue).toFixed(2);
    return null;
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const line_overrides = {};
      for (const ln of lines) {
        line_overrides[String(ln.row_idx)] = {
          include: ln.include !== false,
          decision: ln.decision,
          sku: ln.sku,
          name: ln.name,
          cost: ln.cost,
          sell_price_incl: ln.sell_price_incl, // null → backend uses pricing strategy
          reorder_level: ln.reorder_level,
        };
      }
      const { data } = await api.post("/supplier-pricelist/commit", {
        draft_id: draft.id,
        pricing_mode: pricingMode,
        pricing_value: Number(pricingValue) || null,
        default_reorder_level: defaultReorder === "" ? null : Number(defaultReorder),
        default_vat_rate: defaultVat === "" ? null : Number(defaultVat),
        line_overrides,
      });
      toast.success(`Committed: ${data.totals.new} new · ${data.totals.update} updated · ${data.totals.skipped} skipped.`);
      onCommitted?.(data.totals);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed.");
    } finally { setCommitting(false); }
  };

  const fmtZar = (v) => Number.isFinite(v) ? `R ${v.toFixed(2)}` : "—";

  return (
    <div className="hv-card p-4 space-y-3" data-testid="supplier-pricelist-review">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#1FB6C1]" />
        <h3 className="font-semibold text-white text-sm flex-1">Review price-list extraction</h3>
        <span className="text-[10px] text-[#9aa3b5]">{draft.filename}</span>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <Chip label="Lines" value={stats.included} muted={` of ${stats.total}`} />
        <Chip label="New" value={stats.new} accent="text-amber-300" />
        <Chip label="Update" value={stats.update} accent="text-emerald-300" />
        <Chip label="Cost changed" value={stats.cost_changed} accent={stats.cost_changed ? "text-rose-300" : "text-[#647087]"} />
        {stats.missing_cost > 0 && <Chip label="No cost" value={stats.missing_cost} accent="text-amber-300" />}
      </div>

      {/* Bulk pricing controls */}
      <div className="hv-card p-3 bg-white/[0.02] grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
        <label className="text-[11px] sm:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-0.5">Selling-price rule (new items)</div>
          <select value={pricingMode} onChange={(e) => setPricingMode(e.target.value)} className="hv-input text-sm w-full" data-testid="pricelist-pricing-mode">
            <option value="markup">Cost × (1 + markup%)</option>
            <option value="gp">Cost ÷ (1 − GP%)</option>
            <option value="fixed">Fixed price (all rows)</option>
            <option value="none">Leave blank (set later)</option>
          </select>
        </label>
        {pricingMode !== "none" && (
          <label className="text-[11px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-0.5">
              {pricingMode === "markup" ? "Markup %" : pricingMode === "gp" ? "GP %" : "Price (ZAR)"}
            </div>
            <input type="number" value={pricingValue} onChange={(e) => setPricingValue(e.target.value)} className="hv-input text-sm w-full" data-testid="pricelist-pricing-value" />
          </label>
        )}
        <label className="text-[11px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-0.5">Default reorder</div>
          <input type="number" value={defaultReorder} onChange={(e) => setDefaultReorder(e.target.value)} placeholder="optional" className="hv-input text-sm w-full" data-testid="pricelist-reorder" />
        </label>
        <label className="text-[11px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-0.5">VAT %</div>
          <input type="number" value={defaultVat} onChange={(e) => setDefaultVat(e.target.value)} className="hv-input text-sm w-full" data-testid="pricelist-vat" />
        </label>
      </div>

      {/* Line table */}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#647087] bg-white/[0.02]">
              <th className="px-2 py-1.5 w-8"></th>
              <th className="px-2 py-1.5">Match / SKU</th>
              <th className="px-2 py-1.5">Description</th>
              <th className="px-2 py-1.5 w-24 text-right">Cost</th>
              <th className="px-2 py-1.5 w-24 text-right">Existing cost</th>
              <th className="px-2 py-1.5 w-28 text-right">Selling →</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln) => {
              const sell = ln.sell_price_incl ?? computeSell(ln.cost);
              const isNew = ln.decision === "new";
              const matchBadge = isNew ? (
                <span className="text-[9px] uppercase px-1 py-0.5 rounded bg-amber-500/20 text-amber-200">NEW</span>
              ) : (
                <span className="text-[9px] uppercase px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-200">
                  UPD · {Math.round((ln.match_confidence || 0) * 100)}%
                </span>
              );
              return (
                <tr
                  key={ln.row_idx}
                  className={"border-t border-white/5 " + (ln.include === false ? "opacity-40" : "")}
                  data-testid={`pricelist-line-${ln.row_idx}`}
                >
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={ln.include !== false}
                      onChange={(e) => updateLine(ln.row_idx, { include: e.target.checked })}
                      data-testid={`pricelist-include-${ln.row_idx}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <div className="flex items-center gap-1 mb-0.5">{matchBadge}</div>
                    <input
                      type="text"
                      value={ln.sku || ""}
                      onChange={(e) => updateLine(ln.row_idx, { sku: e.target.value })}
                      className="hv-input text-[10px] py-0.5 px-1 font-mono w-full"
                      placeholder="sku"
                    />
                    {!isNew && (
                      <div className="text-[9px] text-[#647087] truncate max-w-[140px]" title={ln.match_inventory_name}>
                        ↳ {ln.match_inventory_name}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="text"
                      value={ln.name || ""}
                      onChange={(e) => updateLine(ln.row_idx, { name: e.target.value })}
                      className="hv-input text-[11px] py-0.5 px-1 w-full"
                      placeholder="description"
                    />
                    {ln.cost_changed && (
                      <div className="text-[9px] text-rose-300 inline-flex items-center gap-0.5 mt-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" /> cost differs
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={ln.cost ?? ""}
                      onChange={(e) => updateLine(ln.row_idx, { cost: e.target.value === "" ? null : Number(e.target.value) })}
                      className="hv-input text-[11px] py-0.5 px-1 w-20 text-right tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#9aa3b5]">
                    {fmtZar(ln.match_existing_cost)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <input
                      type="number"
                      step="0.01"
                      value={ln.sell_price_incl ?? ""}
                      placeholder={sell != null ? sell.toFixed(2) : ""}
                      onChange={(e) => updateLine(ln.row_idx, { sell_price_incl: e.target.value === "" ? null : Number(e.target.value) })}
                      className="hv-input text-[11px] py-0.5 px-1 w-20 text-right tabular-nums text-emerald-300"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 justify-end pt-2 border-t border-white/5">
        <button onClick={onCancel} className="hv-btn hv-btn-ghost text-xs" data-testid="pricelist-cancel">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <button
          onClick={commit}
          disabled={committing || stats.included === 0}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="pricelist-commit"
        >
          {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Commit {stats.included} line{stats.included === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

function Chip({ label, value, accent = "text-white", muted = "" }) {
  return (
    <span className="hv-card px-2 py-1 inline-flex items-center gap-1">
      <span className="text-[#647087]">{label}</span>
      <b className={accent}>{value}</b>
      {muted && <span className="text-[#647087]">{muted}</span>}
    </span>
  );
}

export default SupplierPricelistUploader;
